import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Snackbar,
  IconButton,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Grid,
  Autocomplete,
  Avatar,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Send as SendIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Support as SupportIcon,
  Info as InfoIcon,
  Note as NoteIcon,
  Edit as EditIcon,
  Forum as ForumIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  FiberNew as FiberNewIcon,
  Autorenew as AutorenewIcon,
  HourglassEmpty as HourglassEmptyIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ArrowDownward as ArrowDownwardIcon,
  Remove as RemoveIcon,
  ArrowUpward as ArrowUpwardIcon,
  PriorityHigh as PriorityHighIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { getTicketsWithUserDetails, getTicket, updateTicket, markTicketAsRead, getCustomers, getTicketsByCustomer } from '../firebase/services';
import { useLanguage } from '../context/LanguageContext';
import { Timestamp } from 'firebase/firestore';

// Interfaces
interface Message {
  id: string;
  text: string;
  createdAt: any;
  userId: string;
  isStaff: boolean;
}
interface Ticket {
  id: string;
  ticketId: string;
  subject: string;
  description: string;
  status: 'new' | 'inProgress' | 'pending' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'critical';
  category: string;
  createdAt: any;
  updatedAt: any;
  userId: string;
  userName?: string;
  userEmail?: string;
  customerId?: string;
  customerName?: string;
  isRead?: boolean;
  internalNotes?: string;
  assignedTo?: string;
  messages?: Message[];
}

interface Customer {
  id: string;
  customerId: string;
  name: string;
  companyName: string;
  status: 'active' | 'inactive';
}

interface TicketStats {
  new: number;
  inProgress: number;
  pending: number;
  resolved: number;
  closed: number;
  total: number;
}

const AgentSupport: React.FC = () => {
  const { currentUser } = useAuth();
  const { t } = useLanguage();

  // States
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [filteredTickets, setFilteredTickets] = useState<Ticket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [showTicketDetails, setShowTicketDetails] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [userFilter, setUserFilter] = useState<string>('all');
  const [customerFilter, setCustomerFilter] = useState<string>('all');
  const [userOptions, setUserOptions] = useState<{id: string, name: string}[]>([]);
  const [customerOptions, setCustomerOptions] = useState<Customer[]>([]);

  // Reply and notes states
  const [replyMessage, setReplyMessage] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [replying, setReplying] = useState(false);

  // Boîte de dialogue pour associer un client
  const [showAssignCustomerDialog, setShowAssignCustomerDialog] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');

  // Ajouter un style global pour l'animation de rotation
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      .rotating-icon {
        animation: spin 1.5s linear infinite;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Fetch tickets and customers on mount
  useEffect(() => { 
    fetchTickets();
    fetchCustomers();
  }, []);
  
  // Apply filters when filters change, but not when customerFilter changes
  // as it triggers a new fetchTickets call
  useEffect(() => { 
    applyFilters(); 
  }, [tickets, searchQuery, statusFilter, priorityFilter, categoryFilter, userFilter]);

  // Add useEffect for customerFilter to trigger fetchTickets when it changes
  useEffect(() => {
    if (customerFilter) {
      fetchTickets();
    }
  }, [customerFilter]);

  // Extract unique users from tickets
  useEffect(() => {
    const users = tickets.reduce((acc: {id: string, name: string}[], ticket) => {
      const userName = ticket.userName || ticket.userEmail || '';
      const userId = ticket.userId;
      if (userName && userId && !acc.some(u => u.id === userId)) {
        acc.push({ id: userId, name: userName });
      }
      return acc;
    }, []);
    setUserOptions(users);
  }, [tickets]);

  // Fetch customers
  const fetchCustomers = async () => {
    try {
      const customers = await getCustomers();
      setCustomerOptions(customers);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  // Stats
  const getTicketStats = (): TicketStats => {
    const stats = { new: 0, inProgress: 0, pending: 0, resolved: 0, closed: 0, total: tickets.length };
    tickets.forEach(t => {
      if (t.status in stats) stats[t.status as keyof Omit<TicketStats,'total'>]++;
    });
    return stats;
  };
  const ticketStats = getTicketStats();

  // Fetch tickets with user details
  const fetchTickets = async () => {
    setLoading(true);
    try {
      if (customerFilter !== 'all') {
        const customerTickets = await getTicketsByCustomer(customerFilter);
        setTickets(customerTickets);
        setFilteredTickets(customerTickets);
      } else {
        const ticketsWithDetails = await getTicketsWithUserDetails();
        setTickets(ticketsWithDetails);
        setFilteredTickets(ticketsWithDetails);
      }
    } catch (error) {
      console.error("Error fetching tickets:", error);
      showNotification('Error fetching tickets','error');
    } finally { 
      setLoading(false); 
    }
  };

  const applyFilters = () => {
    let res = [...tickets];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      res = res.filter(t => t.subject.toLowerCase().includes(q)
        || t.description.toLowerCase().includes(q)
        || t.ticketId.toLowerCase().includes(q)
        || t.userName?.toLowerCase().includes(q)
        || t.userEmail?.toLowerCase().includes(q)
        || t.customerName?.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== 'all') res = res.filter(t => t.status === statusFilter);
    if (priorityFilter !== 'all') res = res.filter(t => t.priority === priorityFilter);
    if (categoryFilter !== 'all') res = res.filter(t => t.category === categoryFilter);
    if (userFilter !== 'all') res = res.filter(t => t.userId === userFilter);
    setFilteredTickets(res);
  };

  const handleViewTicket = async (ticket:Ticket) => {
    try {
      // Récupérer les détails complets du ticket
      const full = (await getTicket(ticket.id)) as Ticket;
      if (!full) throw new Error();
      
      // Copier les informations existantes qui pourraient être manquantes dans la réponse de getTicket
      let enrichedTicket = {
        ...full,
        userName: full.userName || ticket.userName,
        userEmail: full.userEmail || ticket.userEmail,
        customerName: full.customerName || ticket.customerName
      };
      
      // Marquer comme lu si nécessaire
      if (!ticket.isRead) { 
        await markTicketAsRead(ticket.id);
        // Ne pas attendre le résultat de fetchTickets pour éviter le délai
        fetchTickets();
      }
      
      // Si le ticket a un customerId mais pas de customerName, essayer de récupérer le nom
      if (enrichedTicket.customerId && !enrichedTicket.customerName) {
        try {
          // Trouver le client dans les options disponibles
          const customerInfo = customerOptions.find(c => c.id === enrichedTicket.customerId);
          if (customerInfo) {
            enrichedTicket.customerName = customerInfo.companyName || customerInfo.name;
          }
        } catch (error) {
          console.error("Error fetching customer name:", error);
        }
      }
      
      // Mettre à jour l'état et afficher le dialogue
      setSelectedTicket(enrichedTicket);
      setShowTicketDetails(true);
      setInternalNote(enrichedTicket.internalNotes || '');
    } catch (error) {
      console.error("Error viewing ticket:", error);
      showNotification('Error fetching ticket', 'error');
    }
  };

  const handleUpdateStatus = async (status:Ticket['status']) => {
    if (!selectedTicket) return;
    try {
      await updateTicket(selectedTicket.id,{status,updatedAt:Timestamp.now()});
      setSelectedTicket({...selectedTicket,status,updatedAt:Timestamp.now()});
      showNotification('Status updated','success');
      fetchTickets();
    } catch {
      showNotification('Error updating status','error');
    }
  };

  const handleAddReply = async () => {
    if (!selectedTicket || !replyMessage.trim()) return;
    setReplying(true);
    try {
      const msgs = selectedTicket.messages || [];
      const newMsg: Message = { id:`msg_${Date.now()}`, text:replyMessage, createdAt:Timestamp.now(), userId:currentUser?.uid||'admin', isStaff:true };
      await updateTicket(selectedTicket.id,{ messages:[...msgs,newMsg], status:'inProgress', updatedAt:Timestamp.now() });
      setSelectedTicket({...selectedTicket, messages:[...msgs,newMsg], status:'inProgress'});
      setReplyMessage('');
      showNotification('Reply sent','success');
      fetchTickets();
    } catch {
      showNotification('Error sending reply','error');
    } finally {
      setReplying(false);
    }
  };

  const handleSaveInternalNote = async () => {
    if (!selectedTicket) return;
    try {
      await updateTicket(selectedTicket.id,{internalNotes:internalNote,updatedAt:Timestamp.now()});
      setSelectedTicket({...selectedTicket,internalNotes:internalNote,updatedAt:Timestamp.now()});
      showNotification('Internal note saved','success');
    } catch {
      showNotification('Error saving note','error');
    }
  };

  const showNotification = (message:string, severity:'success'|'error') => setNotification({open:true,message,severity});
  const handleCloseNotification = () => setNotification({...notification,open:false});
  const formatDate = (ts:any):string => ts ? new Intl.DateTimeFormat('fr-FR',{dateStyle:'medium',timeStyle:'short'}).format(ts.toDate()) : '';
  
  // Fonctions améliorées pour définir les couleurs et styles des statuts et priorités
  const getStatusInfo = (status: string) => {
    switch(status) {
      case 'new':
        return { 
          color: 'info', 
          bgcolor: '#e3f2fd', 
          icon: <FiberNewIcon fontSize="small" />,
          label: 'Nouveau'
        };
      case 'inProgress':
        return { 
          color: 'primary', 
          bgcolor: '#e8eaf6', 
          icon: <AutorenewIcon fontSize="small" />,
          label: 'En cours'
        };
      case 'pending':
        return { 
          color: 'warning', 
          bgcolor: '#fff8e1', 
          icon: <HourglassEmptyIcon fontSize="small" />,
          label: 'En attente'
        };
      case 'resolved':
        return { 
          color: 'success', 
          bgcolor: '#e8f5e9', 
          icon: <CheckCircleIcon fontSize="small" />,
          label: 'Résolu'
        };
      case 'closed':
        return { 
          color: 'default', 
          bgcolor: '#f5f5f5', 
          icon: <CancelIcon fontSize="small" />,
          label: 'Fermé'
        };
      default:
        return { 
          color: 'default', 
          bgcolor: 'transparent', 
          icon: null,
          label: status
        };
    }
  };

  const getPriorityInfo = (priority: string) => {
    switch(priority) {
      case 'low':
        return { 
          color: 'success', 
          bgcolor: '#e8f5e9', 
          icon: <ArrowDownwardIcon fontSize="small" />,
          label: 'Basse'
        };
      case 'medium':
        return { 
          color: 'info', 
          bgcolor: '#e3f2fd', 
          icon: <RemoveIcon fontSize="small" />,
          label: 'Moyenne'
        };
      case 'high':
        return { 
          color: 'warning', 
          bgcolor: '#fff8e1', 
          icon: <ArrowUpwardIcon fontSize="small" />,
          label: 'Haute'
        };
      case 'critical':
        return { 
          color: 'error', 
          bgcolor: '#ffebee', 
          icon: <PriorityHighIcon fontSize="small" />,
          label: 'Critique'
        };
      default:
        return { 
          color: 'default', 
          bgcolor: 'transparent', 
          icon: null,
          label: priority
        };
    }
  };
  
  // Pour compatibilité avec le code existant
  const getStatusColor = (s:string) => getStatusInfo(s).color as 'info'|'primary'|'warning'|'success'|'error'|'default';
  const getPriorityColor = (p:string) => getPriorityInfo(p).color as 'success'|'info'|'warning'|'error'|'default';

  const handleCustomerFilterChange = (e: SelectChangeEvent) => {
    const newCustomerFilter = e.target.value;
    setCustomerFilter(newCustomerFilter);
    // Le fetchTickets est maintenant géré par le useEffect
  };

  // Fonction pour associer un client à un ticket
  const handleAssignCustomer = async () => {
    if (!selectedTicket || !selectedCustomerId) return;
    
    try {
      await updateTicket(selectedTicket.id, { 
        customerId: selectedCustomerId,
        updatedAt: Timestamp.now()
      });
      
      // Trouver les informations du client sélectionné
      const selectedCustomer = customerOptions.find(c => c.id === selectedCustomerId);
      
      // Mettre à jour le ticket localement
      setSelectedTicket({
        ...selectedTicket,
        customerId: selectedCustomerId,
        customerName: selectedCustomer ? (selectedCustomer.companyName || selectedCustomer.name) : 'Client sans nom'
      });
      
      showNotification('Client associé avec succès', 'success');
      setShowAssignCustomerDialog(false);
      
      // Rafraîchir les tickets
    fetchTickets();
    } catch (error) {
      console.error("Error assigning customer to ticket:", error);
      showNotification("Erreur lors de l'association du client", 'error');
    }
  };

  // Fonction pour ouvrir la boîte de dialogue d'association de client
  const openAssignCustomerDialog = () => {
    // Initialiser avec le client actuel si disponible
    if (selectedTicket?.customerId) {
      setSelectedCustomerId(selectedTicket.customerId);
    } else {
      setSelectedCustomerId('');
    }
    setShowAssignCustomerDialog(true);
  };

  if (loading) return <Box sx={{display:'flex',justifyContent:'center',alignItems:'center',height:'100vh'}}><CircularProgress/></Box>;

  return (
    <Container maxWidth="xl">
      <Box sx={{mt:4,mb:4}}>
        <Typography variant="h4" gutterBottom>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SupportIcon sx={{ fontSize: 32 }} />
            Agent Support Dashboard
          </Box>
        </Typography>
        <Typography 
          variant="subtitle1" 
          color="text.secondary" 
          gutterBottom 
          sx={{ mb: 3, fontStyle: 'italic', maxWidth: '800px' }}
        >
          Gérez efficacement vos tickets de support et maintenez une relation client exceptionnelle
        </Typography>
        {/* Stats */}
        <Grid container spacing={2} sx={{mb:4}}>
          {[
            {value:ticketStats.new,label:'New',color:'info.light'},
            {value:ticketStats.inProgress,label:'In Progress',color:'primary.light'},
            {value:ticketStats.pending,label:'Pending',color:'warning.light'},
            {value:ticketStats.resolved,label:'Resolved',color:'success.light'},
            {value:ticketStats.total,label:'Total Tickets',color:undefined}
          ].map((stat,idx)=>(
            <Grid key={idx} size={{ xs: 12, sm: idx===4?12:6, md: idx===4?4:2 }}>
              <Card sx={stat.color?{bgcolor:stat.color,color:'white'}:{}}>
                <CardContent>
                  <Typography variant="h6">{stat.value}</Typography>
                  <Typography variant="body2">{stat.label}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        {/* Filters */}
        <Paper sx={{p:2,mb:3}}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs:12, md:3 }}>
              <TextField fullWidth size="small" label="Search" value={searchQuery} onChange={e=>setSearchQuery(e.target.value)} InputProps={{startAdornment:<SearchIcon/>}}/>
            </Grid>
            <Grid size={{ xs:12, md:2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={statusFilter} label="Status" onChange={(e:SelectChangeEvent)=>setStatusFilter(e.target.value)}>
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="new">New</MenuItem>
                  <MenuItem value="inProgress">In Progress</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="resolved">Resolved</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs:12, md:1.5 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Priority</InputLabel>
                <Select value={priorityFilter} label="Priority" onChange={(e:SelectChangeEvent)=>setPriorityFilter(e.target.value)}>
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs:12, md:1.5 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select value={categoryFilter} label="Category" onChange={(e:SelectChangeEvent)=>setCategoryFilter(e.target.value)}>
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="technical">Technical</MenuItem>
                  <MenuItem value="billing">Billing</MenuItem>
                  <MenuItem value="account">Account</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs:12, md:2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>User</InputLabel>
                <Select value={userFilter} label="User" onChange={(e:SelectChangeEvent)=>setUserFilter(e.target.value)}>
                  <MenuItem value="all">All Users</MenuItem>
                  {userOptions.map(user => (
                    <MenuItem key={user.id} value={user.id}>{user.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs:12, md:2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Client</InputLabel>
                <Select 
                  value={customerFilter} 
                  label="Client" 
                  onChange={handleCustomerFilterChange}
                  startAdornment={<BusinessIcon sx={{mr:1, ml:-0.5}} fontSize="small" color="action" />}
                >
                  <MenuItem value="all">Tous les clients</MenuItem>
                  {customerOptions.map(customer => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.companyName || customer.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
        {/* Table */}
        <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2, overflow: 'hidden' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Sujet</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Statut</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Priorité</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Catégorie</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Créé le</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Utilisateur</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Client</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTickets.map((ticket, index) => {
                // Déterminer la couleur de fond en fonction du statut et si le ticket a été lu
                let rowBgColor = 'inherit';
                if (!ticket.isRead) {
                  rowBgColor = 'rgba(25, 118, 210, 0.08)'; // Bleu très clair pour non lu
                }
                
                // Couleurs alternées pour améliorer la lisibilité
                const isEven = index % 2 === 0;
                if (rowBgColor === 'inherit') {
                  rowBgColor = isEven ? 'white' : 'rgba(0, 0, 0, 0.02)';
                }
                
                return (
                  <TableRow 
                    key={ticket.id} 
                    sx={{
                      bgcolor: rowBgColor,
                      '&:hover': {
                        bgcolor: 'action.hover',
                        transition: 'background-color 0.2s ease',
                        cursor: 'pointer'
                      },
                      transition: 'background-color 0.2s ease',
                      borderLeft: !ticket.isRead ? '4px solid #1976d2' : 'none'
                    }}
                    onClick={() => handleViewTicket(ticket)}
                  >
                    <TableCell 
                      sx={{ 
                        fontWeight: !ticket.isRead ? 'bold' : 'normal',
                        color: !ticket.isRead ? 'primary.main' : 'inherit'
                      }}
                    >
                      {ticket.ticketId}
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        fontWeight: !ticket.isRead ? 'bold' : 'normal',
                        maxWidth: 250,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {ticket.subject}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const statusInfo = getStatusInfo(ticket.status);
                        return (
                          <Box 
                            sx={{ 
                              display: 'inline-flex',
                              alignItems: 'center',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 2,
                              backgroundColor: statusInfo.bgcolor,
                              color: `${statusInfo.color}.dark`,
                              border: 1,
                              borderColor: `${statusInfo.color}.main`,
                              fontWeight: 'medium',
                              fontSize: '0.75rem',
                              gap: 0.5,
                              minWidth: 95
                            }}
                          >
                            {statusInfo.icon}
                            {statusInfo.label}
                          </Box>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const priorityInfo = getPriorityInfo(ticket.priority);
                        return (
                          <Box 
                            sx={{ 
                              display: 'inline-flex',
                              alignItems: 'center',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 2,
                              backgroundColor: priorityInfo.bgcolor,
                              color: `${priorityInfo.color}.dark`,
                              border: 1,
                              borderColor: `${priorityInfo.color}.main`,
                              fontWeight: 'medium',
                              fontSize: '0.75rem',
                              gap: 0.5,
                              minWidth: 85
                            }}
                          >
                            {priorityInfo.icon}
                            {priorityInfo.label}
                          </Box>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Box 
                        sx={{ 
                          display: 'inline-flex',
                          alignItems: 'center',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 2,
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          color: 'text.primary',
                          border: 1,
                          borderColor: 'divider',
                          fontWeight: 'medium',
                          fontSize: '0.75rem',
                          gap: 0.5,
                          minWidth: 80
                        }}
                      >
                        <CategoryIcon fontSize="small" />
                        {ticket.category}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ 
                      whiteSpace: 'nowrap',
                      color: 'text.secondary',
                      fontSize: '0.875rem'
                    }}>
                      {formatDate(ticket.createdAt)}
                    </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          sx={{ 
                            width: 28, 
                            height: 28, 
                            mr: 1, 
                            bgcolor: !ticket.isRead ? 'primary.main' : 'primary.light',
                            fontSize: '0.875rem'
                          }}
                        >
                          {ticket.userName ? ticket.userName.charAt(0).toUpperCase() : 'U'}
                      </Avatar>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: !ticket.isRead ? 'bold' : 'medium'
                          }}
                        >
                      {ticket.userName || (ticket.userEmail && ticket.userEmail.split('@')[0]) || 'Inconnu'}
                        </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {ticket.customerName ? (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <BusinessIcon 
                            fontSize="small" 
                            sx={{ 
                              mr: 1, 
                              color: 'primary.main' 
                            }} 
                          />
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 'medium',
                              maxWidth: 150,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                        {ticket.customerName}
                          </Typography>
                      </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                          <BusinessIcon fontSize="small" sx={{ mr: 1, opacity: 0.7 }} />
                          <Typography variant="body2">Non associé</Typography>
                        </Box>
                      )}
                  </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          minWidth: '100px',
                          backgroundColor: 'primary.main',
                          color: 'white',
                          boxShadow: 2,
                          borderRadius: '4px',
                          '&:hover': { 
                            backgroundColor: 'primary.dark',
                            boxShadow: 3
                          },
                          transition: 'all 0.2s ease',
                          textTransform: 'none',
                          fontSize: '0.85rem',
                          fontWeight: 'medium',
                          py: 0.8,
                          px: 2
                        }}
                        startIcon={<VisibilityIcon fontSize="small" />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewTicket(ticket);
                        }}
                      >
                        Voir ticket
                      </Button>
                    </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {/* Dialog */}
        <Dialog open={showTicketDetails} onClose={()=>setShowTicketDetails(false)} maxWidth="lg" fullWidth>
          {selectedTicket && <>
            <DialogTitle sx={{ bgcolor: 'primary.light', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SupportIcon />
                <Typography variant="h6">
                  Ticket #{selectedTicket.ticketId}
                </Typography>
                {(() => {
                  const statusInfo = getStatusInfo(selectedTicket.status);
                  return (
                    <Box 
                      sx={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        px: 1.5,
                        py: 0.5,
                        ml: 2,
                        borderRadius: 2,
                        backgroundColor: 'white',
                        color: `${statusInfo.color}.dark`,
                        border: 1,
                        borderColor: `${statusInfo.color}.main`,
                        fontWeight: 'bold',
                        fontSize: '0.75rem',
                        gap: 0.5
                      }}
                    >
                      {statusInfo.icon}
                      {statusInfo.label}
                    </Box>
                  );
                })()}
              </Box>
              <IconButton onClick={()=>setShowTicketDetails(false)} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
              <Grid container>
                {/* Panneau latéral avec informations du ticket */}
                <Grid size={{ xs:12, md:3 }} sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InfoIcon fontSize="small" />
                      Informations
                    </Typography>
                    
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">Statut</Typography>
                        <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                          <Select 
                            value={selectedTicket.status} 
                            onChange={e=>handleUpdateStatus(e.target.value as Ticket['status'])}
                            sx={{ 
                              '& .MuiSelect-select': { 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                py: 1
                              } 
                            }}
                          >
                            <MenuItem value="new" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box 
                                sx={{ 
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  backgroundColor: getStatusInfo('new').bgcolor,
                                  color: `${getStatusInfo('new').color}.dark`,
                                  border: 1,
                                  borderColor: `${getStatusInfo('new').color}.main`,
                                  fontSize: '0.75rem',
                                  gap: 0.5,
                                  minWidth: 90
                                }}
                              >
                                <FiberNewIcon fontSize="small" />
                                Nouveau
                              </Box>
                            </MenuItem>
                            <MenuItem value="inProgress" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box 
                                sx={{ 
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  backgroundColor: getStatusInfo('inProgress').bgcolor,
                                  color: `${getStatusInfo('inProgress').color}.dark`,
                                  border: 1,
                                  borderColor: `${getStatusInfo('inProgress').color}.main`,
                                  fontSize: '0.75rem',
                                  gap: 0.5,
                                  minWidth: 90
                                }}
                              >
                                <AutorenewIcon fontSize="small" />
                                En cours
                              </Box>
                            </MenuItem>
                            <MenuItem value="pending" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box 
                                sx={{ 
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  backgroundColor: getStatusInfo('pending').bgcolor,
                                  color: `${getStatusInfo('pending').color}.dark`,
                                  border: 1,
                                  borderColor: `${getStatusInfo('pending').color}.main`,
                                  fontSize: '0.75rem',
                                  gap: 0.5,
                                  minWidth: 90
                                }}
                              >
                                <HourglassEmptyIcon fontSize="small" />
                                En attente
                              </Box>
                            </MenuItem>
                            <MenuItem value="resolved" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box 
                                sx={{ 
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  backgroundColor: getStatusInfo('resolved').bgcolor,
                                  color: `${getStatusInfo('resolved').color}.dark`,
                                  border: 1,
                                  borderColor: `${getStatusInfo('resolved').color}.main`,
                                  fontSize: '0.75rem',
                                  gap: 0.5,
                                  minWidth: 90
                                }}
                              >
                                <CheckCircleIcon fontSize="small" />
                                Résolu
                              </Box>
                            </MenuItem>
                            <MenuItem value="closed" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box 
                                sx={{ 
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  backgroundColor: getStatusInfo('closed').bgcolor,
                                  color: `${getStatusInfo('closed').color}.dark`,
                                  border: 1,
                                  borderColor: `${getStatusInfo('closed').color}.main`,
                                  fontSize: '0.75rem',
                                  gap: 0.5,
                                  minWidth: 90
                                }}
                              >
                                <CancelIcon fontSize="small" />
                                Fermé
                              </Box>
                            </MenuItem>
                        </Select>
                        </FormControl>
                      </Box>
                      
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">Priorité</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                          {(() => {
                            const priorityInfo = getPriorityInfo(selectedTicket.priority);
                            return (
                              <Box 
                                sx={{ 
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 1,
                                  backgroundColor: priorityInfo.bgcolor,
                                  color: `${priorityInfo.color}.dark`,
                                  border: 1,
                                  borderColor: `${priorityInfo.color}.main`,
                                  fontWeight: 'medium',
                                  fontSize: '0.75rem',
                                  gap: 0.5
                                }}
                              >
                                {priorityInfo.icon}
                                {priorityInfo.label}
                      </Box>
                            );
                          })()}
                        </Box>
                      </Box>
                      
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">Catégorie</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>{selectedTicket.category}</Typography>
                      </Box>
                      
                      <Divider />
                      
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">Créé le</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>{formatDate(selectedTicket.createdAt)}</Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">Dernière mise à jour</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>{formatDate(selectedTicket.updatedAt)}</Typography>
                      </Box>
                      
                      <Divider />
                      
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon fontSize="small" />
                          Utilisateur
                        </Typography>
                        <Paper variant="outlined" sx={{ mt: 1, p: 1.5, borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                              {selectedTicket.userName ? selectedTicket.userName.charAt(0) : 'U'}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {selectedTicket.userName || 'Utilisateur inconnu'}
                              </Typography>
                          {selectedTicket.userEmail && (
                                <Typography variant="caption" color="text.secondary">
                                  {selectedTicket.userEmail}
                                </Typography>
                          )}
                      </Box>
                          </Box>
                        </Paper>
                      </Box>
                      
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BusinessIcon fontSize="small" />
                          Client
                        </Typography>
                        <Paper variant="outlined" sx={{ mt: 1, p: 1.5, borderRadius: 1 }}>
                          {selectedTicket.customerName ? (
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {selectedTicket.customerName}
                              </Typography>
                              <Button 
                                size="small" 
                                variant="text" 
                                color="primary"
                                onClick={openAssignCustomerDialog}
                                startIcon={<EditIcon fontSize="small" />}
                                sx={{ mt: 1, px: 1, py: 0.5 }}
                              >
                                Modifier
                              </Button>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Aucun client associé
                              </Typography>
                              <Button 
                                size="small" 
                                variant="outlined" 
                                color="primary"
                                onClick={openAssignCustomerDialog}
                                startIcon={<BusinessIcon fontSize="small" />}
                              >
                                Associer un client
                              </Button>
                            </Box>
                          )}
                        </Paper>
                      </Box>
                    </Stack>
                  </Box>
                  
                  {/* Notes internes */}
                  <Box sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <NoteIcon fontSize="small" />
                      Notes internes
                    </Typography>
                    <TextField 
                      fullWidth 
                      multiline 
                      rows={6}
                      placeholder="Ajoutez des notes privées ici (visibles uniquement par le support)"
                      value={internalNote} 
                      onChange={e=>setInternalNote(e.target.value)}
                      variant="outlined"
                      size="small"
                      sx={{ mb: 2 }}
                    />
                    <Button 
                      fullWidth 
                      variant="contained" 
                      color="primary"
                      onClick={handleSaveInternalNote}
                      startIcon={<SaveIcon />}
                    >
                      Enregistrer les notes
                    </Button>
                  </Box>
                </Grid>
                
                {/* Panneau principal avec sujet, description et conversation */}
                <Grid size={{ xs:12, md:9 }}>
                  {/* En-tête du ticket */}
                  <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
                    <Typography variant="h5" gutterBottom fontWeight="medium">
                      {selectedTicket.subject}
                    </Typography>
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 2, 
                        mt: 1,
                        borderRadius: 1,
                        bgcolor: 'background.paper'
                      }}
                    >
                      <Typography variant="body1">{selectedTicket.description}</Typography>
                  </Paper>
                  </Box>
                  
                  {/* Conversation */}
                  <Box sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ForumIcon fontSize="small" />
                      Conversation
                    </Typography>
                    
                    <Box sx={{ maxHeight: '400px', overflowY: 'auto', mt: 2, mb: 3, px: 1 }}>
                      <Stack spacing={2}>
                        {selectedTicket.messages && selectedTicket.messages.length > 0 ? (
                          selectedTicket.messages.map(msg => (
                            <Paper 
                              key={msg.id} 
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                ...(msg.isStaff 
                                  ? { 
                                      bgcolor: 'primary.light', 
                                      color: 'white',
                                      ml: 4
                                    } 
                                  : { 
                                      bgcolor: 'grey.100',
                                      mr: 4 
                                    })
                              }}
                              elevation={1}
                            >
                              <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center', 
                                mb: 1 
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Avatar 
                                    sx={{ 
                                      width: 28, 
                                      height: 28, 
                                      bgcolor: msg.isStaff ? 'primary.dark' : 'grey.400'
                                    }}
                                  >
                                    {msg.isStaff ? 'S' : (selectedTicket.userName?.charAt(0) || 'U')}
                                  </Avatar>
                                  <Typography variant="subtitle2">
                                    {msg.isStaff ? 'Support' : selectedTicket.userName}
                                  </Typography>
                                </Box>
                                <Typography variant="caption" color={msg.isStaff ? 'primary.contrastText' : 'text.secondary'}>
                                  {formatDate(msg.createdAt)}
                                </Typography>
                              </Box>
                              <Typography 
                                variant="body1" 
                                sx={{ 
                                  whiteSpace: 'pre-wrap',
                                  color: msg.isStaff ? 'primary.contrastText' : 'text.primary'
                                }}
                              >
                                {msg.text}
                              </Typography>
                            </Paper>
                          ))
                        ) : (
                          <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography color="text.secondary">
                              Aucun message dans cette conversation
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </Box>
                    
                    {/* Formulaire de réponse */}
                    <Paper 
                      variant="outlined" 
                      sx={{ p: 2, borderRadius: 1, bgcolor: 'background.paper' }}
                    >
                      <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                        Répondre
                      </Typography>
                      <TextField 
                        fullWidth 
                        multiline 
                        rows={4} 
                        placeholder="Tapez votre message ici..."
                        value={replyMessage} 
                        onChange={e=>setReplyMessage(e.target.value)} 
                        disabled={replying}
                        variant="outlined"
                        size="small"
                        sx={{ mb: 2 }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button 
                          variant="contained" 
                          color="primary" 
                          onClick={handleAddReply} 
                          disabled={!replyMessage.trim()||replying} 
                          startIcon={replying ? <AutorenewIcon className="rotating-icon" /> : <SendIcon />}
                          sx={{ 
                            borderRadius: 8,
                            px: 3,
                            py: 1,
                            background: 'linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)',
                            boxShadow: '0 3px 5px 2px rgba(76, 175, 80, .3)',
                            '&:hover': { 
                              background: 'linear-gradient(45deg, #388E3C 30%, #689F38 90%)',
                              transform: 'translateY(-2px)',
                              boxShadow: '0 6px 10px 2px rgba(76, 175, 80, .3)'
                            },
                            '&:disabled': {
                              background: 'rgba(0, 0, 0, 0.12)',
                              boxShadow: 'none'
                            },
                            transition: 'all 0.2s ease',
                            textTransform: 'none',
                            fontWeight: 'bold'
                          }}
                        >
                          {replying ? 'Envoi en cours...' : 'Envoyer'}
                        </Button>
                      </Box>
                    </Paper>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
          </>}
        </Dialog>
        <Snackbar open={notification.open} autoHideDuration={6000} onClose={handleCloseNotification} anchorOrigin={{vertical:'bottom',horizontal:'right'}}><Alert onClose={handleCloseNotification} severity={notification.severity} variant="filled">{notification.message}</Alert></Snackbar>
        
        {/* Boîte de dialogue pour associer un client */}
        <Dialog open={showAssignCustomerDialog} onClose={() => setShowAssignCustomerDialog(false)}>
          <DialogTitle>Associer un client au ticket</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Sélectionnez le client à associer à ce ticket
            </DialogContentText>
            <FormControl fullWidth>
              <InputLabel id="assign-customer-label">Client</InputLabel>
              <Select
                labelId="assign-customer-label"
                value={selectedCustomerId}
                onChange={(e) => setSelectedCustomerId(e.target.value)}
                label="Client"
                startAdornment={<BusinessIcon sx={{ mr: 1, ml: -0.5 }} fontSize="small" color="action" />}
              >
                <MenuItem value="">
                  <em>Aucun</em>
                </MenuItem>
                {customerOptions.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.companyName || customer.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowAssignCustomerDialog(false)}>Annuler</Button>
            <Button 
              onClick={handleAssignCustomer} 
              color="primary" 
              disabled={!selectedCustomerId}
            >
              Associer
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default AgentSupport;
